// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SERIAL_PATTERN_KIND_DISPLAY } from '@/account/utilities/serial-pattern-display';
import type {
  CachedSerialPatternDeltaListProperty,
  CachedSerialPatternItem,
} from '@/audit/api/types';
import { Stack, Typography } from '@mui/material';

/** The word shown where a kind's length is set by the format, not the user. */
const FIXED_LENGTH_LABEL = 'fixed';

interface Props {
  property: CachedSerialPatternDeltaListProperty;
}

export function AuditSerialPatternDeltaListLabel(props: Props) {
  const added = props.property.newValue?.added ?? [];
  const removed = props.property.newValue?.removed ?? [];

  if (added.length === 0 && removed.length === 0) {
    return null;
  }

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>
      {added.map((pattern) => (
        <SerialPatternDeltaItem
          key={`added-${pattern.expression}`}
          pattern={pattern}
          change="added"
        />
      ))}
      {removed.map((pattern) => (
        <SerialPatternDeltaItem
          key={`removed-${pattern.expression}`}
          pattern={pattern}
          change="removed"
        />
      ))}
    </Stack>
  );
}

interface ItemProps {
  pattern: CachedSerialPatternItem;
  change: 'added' | 'removed';
}

/** One pattern in the delta. */
function SerialPatternDeltaItem(props: ItemProps) {
  const kind = props.pattern.kind;
  const display = kind === null ? null : SERIAL_PATTERN_KIND_DISPLAY[kind];

  return (
    <Stack marginLeft={2}>
      <Typography variant="caption">
        <Typography component="span" variant="inherit">
          Pattern: {display === null ? props.pattern.expression : display.label}
        </Typography>{' '}
        <Typography
          component="span"
          variant="inherit"
          sx={{ textDecoration: 'underline' }}
        >
          {props.change}
        </Typography>
      </Typography>
      <Stack marginLeft={2}>
        {props.pattern.prefix !== null && (
          <Typography variant="caption">
            Prefix: {props.pattern.prefix}
          </Typography>
        )}
        {display !== null && (
          <Typography variant="caption">
            Length: {props.pattern.length ?? FIXED_LENGTH_LABEL}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}
